import type { OutputReference } from "@smartsuite/automation-engine-api";
import type { PillNodeAttrs } from "@smartsuite/smartdoc/lib/esm/extensions/pill/extension";
import type {
  ActionCandidate,
  TriggerCandidate,
} from "../../../../../../../../types/candidate";
import { CURRENT_ITEM_OUTPUT_ID } from "../../../../../../../../utils/constants";
import type { LoopingStore } from "../../../../../../../../stores/automation/looping.store";

type LoopingSourceContext = Pick<LoopingStore, "source">;

export interface MakeAutomationPillAttrsContext {
  trigger: TriggerCandidate;
  actions: ActionCandidate[];
  action?: ActionCandidate;
  looping: LoopingSourceContext;
  makeTriggerPillTooltip: (trigger: TriggerCandidate) => {
    title: string;
    text: string | undefined;
  };
  makeActionPillTooltip: (action: ActionCandidate) => {
    title: string;
    text: string | undefined;
  };
}

/**
 * Coerces TriggerDropdown shorthand `{ value, target }` into engine `OutputReference` JSON.
 * Idempotent when `attrs.value` is already an `OutputReference`.
 */
export const makeAutomationPillAttrs = (
  attrs: PillNodeAttrs,
  ctx: MakeAutomationPillAttrsContext,
): PillNodeAttrs => {
  try {
    const pillValue = JSON.parse(String(attrs.value)) as {
      target?: string;
      value?: string;
      hash?: string;
      outputId?: string;
      source?: unknown;
    };

    if (
      typeof pillValue.outputId === "string" &&
      pillValue.source !== undefined
    ) {
      return attrs;
    }

    const { trigger, actions, makeTriggerPillTooltip, makeActionPillTooltip } =
      ctx;

    if (pillValue.target === "trigger" && trigger.triggerReference) {
      const outputReference: OutputReference = {
        source: {
          oneofKind: "triggerReference",
          triggerReference: trigger.triggerReference,
        },
        outputId: String(pillValue.value),
      };
      return {
        ...attrs,
        value: JSON.stringify(outputReference),
        tooltip: makeTriggerPillTooltip(trigger),
      };
    }

    if (pillValue.target === "loop") {
      const loopSource = ctx.looping.source;

      if (loopSource?.oneofKind === "outputReference") {
        switch (loopSource.outputReference.source.oneofKind) {
          case "actionReference":
            return {
              ...attrs,
              value: JSON.stringify({
                outputId: CURRENT_ITEM_OUTPUT_ID,
                source: {
                  oneofKind: "actionReference",
                  actionReference:
                    loopSource.outputReference.source.actionReference,
                },
              } satisfies OutputReference),
            };
          case "triggerReference":
            return {
              ...attrs,
              value: JSON.stringify({
                outputId: CURRENT_ITEM_OUTPUT_ID,
                source: {
                  oneofKind: "triggerReference",
                  triggerReference:
                    loopSource.outputReference.source.triggerReference,
                },
              } satisfies OutputReference),
            };
          case "loopItem":
          default:
            return attrs;
        }
      }
    }

    if (pillValue.target === "action") {
      // The reference is built from the SOURCE action (found by hash), so it
      // must NOT depend on the action currently being edited: the field
      // editor's `actions` list is the upstream sources and excludes the
      // edited action, so `ctx.action` is undefined for actions inside a loop
      // (currentAction = actions.find(hash === edited) never matches). Gating
      // on it left the pill as raw `{value,target,hash}` shorthand, which the
      // field-missing validation then rejected as an invalid reference.
      const targetAction = actions.find((a) => a.hash === pillValue.hash);
      if (targetAction?.actionReference !== undefined) {
        const outputReference: OutputReference = {
          source: {
            oneofKind: "actionReference",
            actionReference: targetAction.actionReference,
          },
          outputId: String(pillValue.value),
        };
        return {
          ...attrs,
          value: JSON.stringify(outputReference),
          tooltip: makeActionPillTooltip(targetAction),
        };
      }
    }
  } catch {
    // leave attrs unchanged
  }
  return attrs;
};
